import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InfoTab } from '../components/tabs/InfoTab';
import { PlansTab } from '../components/tabs/PlansTab';
import { NotesTab } from '../components/tabs/NotesTab';
import { RemindersTab } from '../components/tabs/RemindersTab';
import { AppointmentsTab } from '../components/tabs/AppointmentsTab';
import { MeasurementsTab } from '../components/tabs/MeasurementsTab';
import { BloodParametersTab } from '../components/tabs/BloodParametersTab';
import { SupplementsTab } from '../components/tabs/SupplementsTab';
import { PaymentsTab } from '../components/tabs/PaymentsTab';
import { TimelineTab } from '../components/tabs/TimelineTab';
import { useClientDetails } from '../hooks/useClientDetails';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import { candidateJidsForPhone } from '../../lib/whatsapp/jid';
import type { ClientMedium } from '../types/clients';

type TabType = 'info' | 'plans' | 'notes' | 'reminders' | 'appointments' | 'measurements' | 'blood_parameters' | 'supplements' | 'payments' | 'timeline';
type ClientActivityStatus = 'active' | 'past';

const clientStatusOptions: { value: ClientActivityStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'past', label: 'Inactive' },
];

const mediumOptions: { value: ClientMedium; label: string }[] = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

function mapClientStatus(status: string | null | undefined): ClientActivityStatus | null {
  if (!status) return null;
  return status === 'active' ? 'active' : 'past';
}

export function ClientDetails() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const resolvedClientId = clientId ?? '';
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const clientQuery = useClientDetails(resolvedClientId);

  // Get active tab from URL params, default to 'info'
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const validTabs: TabType[] = ['info', 'plans', 'notes', 'reminders', 'appointments', 'measurements', 'blood_parameters', 'supplements', 'payments', 'timeline'];
  const activeTab = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'info';

  const [clientStatusOverride, setClientStatusOverride] = useState<ClientActivityStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [mediumOverride, setMediumOverride] = useState<ClientMedium | null>(null);
  const [mediumError, setMediumError] = useState<string | null>(null);
  const clientData = clientQuery.data;

  // Calculate the WhatsApp JID for navigation
  const whatsappJid = useMemo(() => {
    if (!clientData) return null;
    const candidateJids = candidateJidsForPhone(clientData.phone, clientData.whatsapp_jid);
    return candidateJids[0] ?? null;
  }, [clientData]);

  const baseClientStatus = mapClientStatus(clientData?.status ?? null);

  useEffect(() => {
    setClientStatusOverride(null);
  }, [baseClientStatus]);

  const clientStatusMutation = useMutation({
    mutationFn: async (nextStatus: ClientActivityStatus) => {
      const response = await apiRequest(`/api/clients/${resolvedClientId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      }, session?.access_token);
      return response;
    },
    onSuccess: (_, nextStatus) => {
      queryClient.invalidateQueries({ queryKey: ['client', resolvedClientId] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['timeline', resolvedClientId] });
      setClientStatusOverride(nextStatus);
      setStatusError(null);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to update status';
      setStatusError(message);
    },
  });

  const displayedClientStatus = clientStatusOverride ?? baseClientStatus;
  const clientControlsDisabled = clientStatusMutation.isPending;

  const handleClientStatusChange = async (nextStatus: ClientActivityStatus) => {
    if (clientControlsDisabled) return;
    if (nextStatus === baseClientStatus && !clientStatusOverride) return;
    setStatusError(null);
    setClientStatusOverride(nextStatus);
    try {
      await clientStatusMutation.mutateAsync(nextStatus);
    } catch {
      setClientStatusOverride(null);
    }
  };

  const mediumMutation = useMutation({
    mutationFn: async (nextMedium: ClientMedium) => {
      const response = await apiRequest(`/api/clients/${resolvedClientId}`, {
        method: 'PATCH',
        body: JSON.stringify({ medium: nextMedium }),
      }, session?.access_token);
      return response;
    },
    onSuccess: (_, nextMedium) => {
      queryClient.invalidateQueries({ queryKey: ['client', resolvedClientId] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['timeline', resolvedClientId] });
      setMediumOverride(nextMedium);
      setMediumError(null);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to update medium';
      setMediumError(message);
    },
  });

  const baseMedium = (clientData?.medium as ClientMedium) || 'online';
  const displayedMedium = mediumOverride ?? baseMedium;

  const handleMediumChange = async (nextMedium: ClientMedium) => {
    if (mediumMutation.isPending) return;
    if (nextMedium === baseMedium && !mediumOverride) return;
    setMediumError(null);
    setMediumOverride(nextMedium);
    try {
      await mediumMutation.mutateAsync(nextMedium);
    } catch {
      setMediumOverride(null);
    }
  };

  // Build tabs based on profession configuration
  const profession = useAuth().profile?.profession;

  const tabs = [
    {
      key: 'info',
      label: 'Info'
    },
    // Conditionally include plans tab
    ...(profession?.show_plans_tab ? [{
      key: 'plans' as const,
      label: profession?.label_plans || 'Plans'
    }] : []),
    {
      key: 'notes',
      label: 'Notes'
    },
    {
      key: 'reminders',
      label: 'Reminders'
    },
    {
      key: 'appointments',
      label: 'Appointments'
    },
    // Conditionally include measurements tab
    ...(profession?.show_measurements_tab ? [{
      key: 'measurements' as const,
      label: profession?.label_measurements || 'Measurements'
    }] : []),
    // Conditionally include blood parameters tab
    ...(profession?.show_blood_parameters_tab ? [{
      key: 'blood_parameters' as const,
      label: profession?.label_blood_parameters || 'Blood Parameters'
    }] : []),
    // Conditionally include supplements tab
    ...(profession?.show_supplements_tab ? [{
      key: 'supplements' as const,
      label: profession?.label_supplements || 'Supplements'
    }] : []),
    {
      key: 'payments',
      label: 'Payments'
    },
    {
      key: 'timeline',
      label: 'Timeline'
    }
  ] as const;
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleNavigateToTab = (tab: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace: true });
  };
  if (clientQuery.isLoading) {
    return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading client...</p>
        </div>
      </div>;
  }

  if (clientQuery.isError || !clientData) {
    return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md">
          <p className="text-lg font-semibold text-gray-800 mb-2">
            Unable to load client
          </p>
          <p className="text-gray-600 mb-6">
            Please go back to the clients list and try again.
          </p>
          <button onClick={() => navigate('/clients')} className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-medium">
            Back to Clients
          </button>
        </div>
      </div>;
  }

  return <div className="min-h-screen bg-[#F5F5F5]">
      <div className="bg-white shadow-sm sticky top-0 z-30">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-800">
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <div className="w-12 h-12 bg-[#5B8E87] bg-opacity-10 rounded-full flex items-center justify-center flex-shrink-0">
              {clientData.avatar_url ? <img src={clientData.avatar_url} alt={clientData.name} className="w-12 h-12 rounded-full object-cover" /> : <span className="text-[#5B8E87] font-semibold">
                  {getInitials(clientData.name)}
                </span>}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-[#212121] truncate">
                {clientData.name}
              </h1>
              {clientData.phone && <a href={`tel:${clientData.phone}`} className="text-xs text-[#5B8E87] flex items-center gap-1">
                  <PhoneIcon className="w-3 h-3" />
                  {clientData.phone}
                </a>}
            </div>
            <button
              onClick={() => whatsappJid && navigate(`/whatsapp/chat/${encodeURIComponent(whatsappJid)}`)}
              className="bg-[#5B8E87] text-white p-3 rounded-lg hover:bg-[#4F7A74] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Chat"
              disabled={!whatsappJid}
            >
              <MessageCircleIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-1 rounded-full bg-gray-100 p-0.5">
              {clientStatusOptions.map((option) => {
                const isActive = displayedClientStatus === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-white text-[#5B8E87] shadow-sm'
                        : 'text-gray-600'
                    } ${clientControlsDisabled ? 'opacity-70 cursor-not-allowed' : 'hover:bg-white/50'}`}
                    onClick={() => handleClientStatusChange(option.value)}
                    disabled={clientControlsDisabled}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-1 rounded-full bg-gray-100 p-0.5">
              {mediumOptions.map((option) => {
                const isActive = displayedMedium === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-white text-[#5B8E87] shadow-sm'
                        : 'text-gray-600'
                    } ${mediumMutation.isPending ? 'opacity-70 cursor-not-allowed' : 'hover:bg-white/50'}`}
                    onClick={() => handleMediumChange(option.value)}
                    disabled={mediumMutation.isPending}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {(clientStatusMutation.isPending || mediumMutation.isPending) && (
              <span className="text-xs text-gray-500">Updating…</span>
            )}

            {(statusError || mediumError) && (
              <div className="w-full">
                {statusError && (
                  <span className="text-xs text-red-500 block">{statusError}</span>
                )}
                {mediumError && (
                  <span className="text-xs text-red-500 block">{mediumError}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex overflow-x-auto px-4 pb-3 gap-2 scrollbar-hide border-t border-gray-100 pt-3">
          {tabs.map(tab => <button key={tab.key} onClick={() => handleNavigateToTab(tab.key)} className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition-colors ${activeTab === tab.key ? 'bg-[#5B8E87] text-white' : 'bg-gray-100 text-gray-600'}`}>
              {tab.label}
            </button>)}
        </div>
      </div>
      <div className="pb-20">
        {activeTab === 'info' && <InfoTab clientId={resolvedClientId} onNavigateToTab={handleNavigateToTab} />}
        {activeTab === 'plans' && <PlansTab clientId={resolvedClientId} planIdFromUrl={searchParams.get('planId')} />}
        {activeTab === 'notes' && <NotesTab clientId={resolvedClientId} />}
        {activeTab === 'reminders' && <RemindersTab clientId={resolvedClientId} onNavigateToChat={() => whatsappJid && navigate(`/whatsapp/chat/${encodeURIComponent(whatsappJid)}`)} />}
        {activeTab === 'appointments' && <AppointmentsTab clientId={resolvedClientId} />}
        {activeTab === 'measurements' && <MeasurementsTab clientId={resolvedClientId} />}
        {activeTab === 'blood_parameters' && <BloodParametersTab clientId={resolvedClientId} />}
        {activeTab === 'supplements' && <SupplementsTab clientId={resolvedClientId} />}
        {activeTab === 'payments' && <PaymentsTab clientId={resolvedClientId} clientName={clientData?.name} clientPhone={clientData?.phone} onNavigateToTab={handleNavigateToTab} />}
        {activeTab === 'timeline' && <TimelineTab clientId={resolvedClientId} onNavigateToTab={handleNavigateToTab} />}
      </div>
    </div>;
}
